import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { AlertTriangle, Info } from 'lucide-react';
import { SlideOver, Button, Loader } from '../../components';
import { appUsageService } from '../../services';

/**
 * One carer's mobile app usage — opened from Staff Management's 3-dot menu
 * ("View app usage"), which offers it on carer rows to managers and owners only.
 *
 * The phones time it: a session runs from opening the app (signed in) to
 * leaving it, and is reported once, when they leave. So today's figure moves as
 * the carer puts the phone down, not live by the second, and a session that
 * couldn't be sent (no signal, phone died) arrives the next time they open the
 * app. Nothing is estimated here — see api/utils/appUsage.js.
 *
 * "No data yet" is deliberately not shown as 0 min: it means this person's app
 * has never reported anything — an older app version, or not signed in since
 * the update — which is a different conversation from "didn't use it".
 */

const RANGES = [
  { key: 'today', label: 'Today', days: 1 },
  { key: '7', label: '7 days', days: 7 },
  { key: '30', label: '30 days', days: 30 },
  { key: '90', label: '90 days', days: 90 },
];

const UK = 'Europe/London';

/** Today's date in the UK — the same "today" the API files sessions under. */
const ukToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: UK }).format(new Date());

const addDays = (dateStr, n) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
};

/** "Mon 27 Oct" for a YYYY-MM-DD date. */
const dayLabel = (dateStr, opts = { weekday: 'short', day: 'numeric', month: 'short' }) =>
  new Date(`${dateStr}T00:00:00Z`).toLocaleDateString('en-GB', { ...opts, timeZone: 'UTC' });

/** "14:32" in UK time for an instant. */
const timeLabel = (iso) =>
  new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: UK });

const ukDateOf = (iso) => new Intl.DateTimeFormat('en-CA', { timeZone: UK }).format(new Date(iso));

/** "2h 15m", "45 min", "<1 min". */
const duration = (seconds) => {
  const s = Number(seconds) || 0;
  if (s <= 0) return '0 min';
  if (s < 60) return '<1 min';
  const mins = Math.round(s / 60);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
};

/** "Today 14:32", "Yesterday 09:10", "3 Oct, 14:32". */
const lastUsedLabel = (iso) => {
  if (!iso) return null;
  const date = ukDateOf(iso);
  const today = ukToday();
  if (date === today) return `Today ${timeLabel(iso)}`;
  if (date === addDays(today, -1)) return `Yesterday ${timeLabel(iso)}`;
  return `${dayLabel(date, { day: 'numeric', month: 'short' })}, ${timeLabel(iso)}`;
};

const PLATFORM = { ios: 'iPhone', android: 'Android' };

/** A round axis top a little above the tallest bar. */
const axisTop = (maxSeconds) => {
  const mins = maxSeconds / 60;
  const steps = [15, 30, 60, 120, 180, 240, 360, 480, 600, 720];
  const step = steps.find((s) => mins <= s);
  return (step || Math.ceil(mins / 600) * 600) * 60;
};

/**
 * Time per day as bars — one series, so no legend: the heading names it. Each
 * column is the hover target (bigger than the bar), with a tooltip giving the
 * exact figure. The session list below is the readable view of the same time.
 */
const DailyBars = ({ days, height = 120 }) => {
  const [hover, setHover] = useState(null);
  const top = axisTop(Math.max(0, ...days.map((d) => d.seconds)));
  const n = days.length;
  const labelEvery = Math.max(1, Math.ceil(n / 7));

  return (
    <div className="pt-5">
      <div className="relative" style={{ height }}>
        <div className="absolute inset-x-0 top-0 border-t border-dashed border-[#E2E8F0] dark:border-gray-700" />
        <div className="absolute inset-x-0 top-1/2 border-t border-dashed border-[#F1F5F9] dark:border-gray-800" />
        <span className="absolute left-0 -top-5 text-[11px] text-[#94A3B8] dark:text-gray-500 tabular-nums">
          {duration(top)}
        </span>
        <div className="absolute inset-0 flex items-end gap-[2px] border-b border-[#E2E8F0] dark:border-gray-700">
          {days.map((d, i) => {
            const align = i < n / 4 ? 'left-0' : i >= (3 * n) / 4 ? 'right-0' : 'left-1/2 -translate-x-1/2';
            return (
              <div
                key={d.date}
                className="relative flex-1 h-full flex items-end justify-center"
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
              >
                <div
                  className={`w-full max-w-[44px] rounded-t-[4px] bg-blue-600 dark:bg-blue-500 transition-opacity ${hover !== null && hover !== i ? 'opacity-60' : ''}`}
                  style={{ height: d.seconds > 0 ? `max(2px, ${(d.seconds / top) * 100}%)` : 0 }}
                />
                {hover === i && (
                  <div className={`absolute bottom-full mb-1.5 z-10 ${align} whitespace-nowrap rounded-lg bg-[#0B1220] dark:bg-gray-100 px-2.5 py-1.5 shadow-lg pointer-events-none`}>
                    <div className="text-[11px] text-gray-300 dark:text-gray-600">{dayLabel(d.date)}</div>
                    <div className="text-[13px] font-bold text-white dark:text-[#0B1220] tabular-nums">{duration(d.seconds)}</div>
                    <div className="text-[11px] text-gray-300 dark:text-gray-600">
                      {d.sessions} {d.sessions === 1 ? 'session' : 'sessions'}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <div className="relative h-5 mt-1">
        {days.map((d, i) => (i % labelEvery === 0 || i === n - 1) && (
          <span
            key={d.date}
            className={`absolute text-[11px] text-[#94A3B8] dark:text-gray-500 whitespace-nowrap ${i === 0 ? '' : i === n - 1 ? '-translate-x-full' : '-translate-x-1/2'}`}
            style={{ left: i === 0 ? 0 : i === n - 1 ? '100%' : `${((i + 0.5) / n) * 100}%` }}
          >
            {dayLabel(d.date, { day: 'numeric', month: 'short' })}
          </span>
        ))}
      </div>
    </div>
  );
};

/**
 * @param {object|null} staff  the Staff Management row: { id, name, user_id }
 */
const AppUsagePanel = ({ staff, onClose }) => {
  const [rangeKey, setRangeKey] = useState('7');
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  const range = useMemo(() => {
    const today = ukToday();
    const days = RANGES.find((r) => r.key === rangeKey)?.days || 7;
    return { from: addDays(today, -(days - 1)), to: today };
  }, [rangeKey]);

  // A different person starts on the default range, with nothing of the last
  // person's left on screen while theirs loads.
  useEffect(() => {
    setRangeKey('7');
    setDetail(null);
  }, [staff?.id]);

  useEffect(() => {
    if (!staff) {
      setDetail(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    appUsageService.getPerson(staff.id, range)
      .then((res) => { if (!cancelled) setDetail(res?.data || null); })
      .catch((error) => {
        if (!cancelled) {
          setDetail(null);
          toast.error(error?.response?.data?.message || 'Could not load app usage');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [staff, range]);

  const person = detail?.person;

  return (
    <SlideOver
      isOpen={!!staff}
      onClose={onClose}
      title={staff ? `App usage — ${staff.name}` : ''}
      subtitle={staff ? <span className="font-mono">{staff.user_id}</span> : null}
      footer={(
        <div className="flex justify-end">
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </div>
      )}
    >
      <div className="space-y-5">
        <div className="flex items-center gap-1 p-1 rounded-full bg-[#F1F5F9] dark:bg-[#31373f] w-fit">
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              onClick={() => setRangeKey(r.key)}
              className={`px-3 py-1.5 rounded-full text-[12.5px] font-semibold whitespace-nowrap transition ${rangeKey === r.key
                ? 'bg-white dark:bg-[#262b33] text-blue-600 dark:text-blue-400 shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
            >
              {r.label}
            </button>
          ))}
        </div>

        {loading && !person ? (
          <div className="py-12"><Loader text="Loading app usage..." /></div>
        ) : person && !person.tracked ? (
          <div className="flex items-start gap-3 rounded-xl border border-[#FBE3C0] dark:border-amber-900/50 bg-[#FFFBEB] dark:bg-amber-900/10 p-4">
            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <p className="text-[13px] text-amber-800 dark:text-amber-300">
              <strong>No app usage recorded yet.</strong> Usage is only recorded by the latest
              version of the app — ask {person.name} to update it and sign in once. Their time
              is counted from then on.
            </p>
          </div>
        ) : person ? (
          <>
            <div className="grid grid-cols-2 gap-3">
              {[
                [rangeKey === 'today' ? 'Time in the app today' : 'Time in the app', duration(person.total_seconds)],
                ['Days used', `${person.days_active} of ${(detail.daily || []).length}`],
                ['Sessions', person.sessions],
                ['Last used', lastUsedLabel(person.last_used_at) || '—'],
              ].map(([label, value]) => (
                <div key={label} className="rounded-xl border border-[#E2E8F0] dark:border-gray-800 p-3">
                  <div className="text-[18px] font-extrabold text-[#0B1220] dark:text-gray-100 tabular-nums">{value}</div>
                  <div className="text-[12px] text-[#64748B] dark:text-gray-400">{label}</div>
                </div>
              ))}
            </div>

            {(detail.daily || []).length > 1 && (
              <div>
                <div className="text-[13px] font-bold text-[#0B1220] dark:text-gray-100">Time in the app per day</div>
                <DailyBars days={detail.daily} />
              </div>
            )}

            <div>
              <div className="text-[13px] font-bold text-[#0B1220] dark:text-gray-100 mb-2">Sessions</div>
              {(detail.sessions || []).length === 0 ? (
                <p className="text-[13px] text-[#64748B] dark:text-gray-400">No sessions in this period.</p>
              ) : (
                <ul className="divide-y divide-[#E2E8F0] dark:divide-gray-800 rounded-xl border border-[#E2E8F0] dark:border-gray-800">
                  {detail.sessions.map((s) => (
                    <li key={`${s.started_at}-${s.seconds}`} className="flex items-center justify-between gap-3 px-3 py-2.5">
                      <div className="min-w-0">
                        <div className="text-[13px] font-semibold text-gray-800 dark:text-gray-200">
                          {dayLabel(ukDateOf(s.started_at))}
                          <span className="font-normal text-[#64748B] dark:text-gray-400">
                            {' · '}{timeLabel(s.started_at)}–{timeLabel(s.ended_at)}
                          </span>
                        </div>
                        {s.platform && (
                          <div className="text-[11.5px] text-[#94A3B8] dark:text-gray-500">{PLATFORM[s.platform] || s.platform}</div>
                        )}
                      </div>
                      <span className="text-[13px] font-bold text-[#0B1220] dark:text-gray-100 tabular-nums shrink-0">
                        {duration(s.seconds)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {detail.sessions_truncated && (
                <p className="text-[12px] text-[#94A3B8] mt-2">Showing the latest 300 sessions. Pick a shorter range to see the rest.</p>
              )}
            </div>

            <p className="flex items-start gap-1.5 text-[12px] text-[#64748B] dark:text-gray-400">
              <Info className="w-4 h-4 shrink-0 mt-px" />
              Counted while the app is open on screen and signed in. Each session is sent when
              the carer leaves the app, so today&apos;s time updates as they put the phone down.
            </p>
          </>
        ) : null}
      </div>
    </SlideOver>
  );
};

export default AppUsagePanel;
